/**
 * /settings - Customer account settings (OCP-477). Three tabs:
 *   - Profile   - account display name + company (account-level identity)
 *   - Security  - 2FA (TOTP) enrollment + password change + session count
 *   - Team      - list / invite / role / remove the account's portal users
 *
 * Tab state is held in the URL `?tab=` so a customer can deep-link to a
 * specific section. Replaces the pre-OCP-477 "coming soon" placeholder;
 * the backend surface (TOTP begin/confirm/disable, password change,
 * profile, team) is fully live behind /v1/portal/settings/*.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useSearchParams } from 'react-router-dom';

import { LanguageSection } from '../components/LanguageSection';
import { ApiError, api } from '../lib/api';
import {
  BUSINESS_CAPACITY_PATH,
  BusinessCapacityForm,
  BusinessCapacityRefusalNotice,
  canDeclareBusinessCapacity,
  formatDeclarationDate,
  type BusinessCapacityResponse,
} from '../components/BusinessCapacityGate';
import { TermsSettingsTab } from '../components/TermsAcceptanceGate';
import {
  RESPONSIBLE_CONTACT_PATH,
  ResponsibleContactModal,
  canManageResponsibleContact,
  type ResponsibleContactResponse,
} from '../components/ResponsibleContactGate';
import { fmtDateTime } from '../lib/format-time';

type PortalRole = 'owner' | 'admin' | 'viewer' | 'billing-only';

/** The customer-facing role label from the `settings` catalogue. */
function roleLabel(t: TFunction<'settings'>, role: PortalRole): string {
  return t(`role.${role}`);
}

/** Roles an invite / role-change can grant (owner is the account holder). */
const INVITABLE: Array<Exclude<PortalRole, 'owner'>> = ['admin', 'viewer', 'billing-only'];

function canManage(role: PortalRole | null): boolean {
  return role === 'owner' || role === 'admin';
}

type Tab = 'profile' | 'contact' | 'business' | 'terms' | 'security' | 'team';
// OCP-524 contact = the authorized responsible contact person required by the
// Terms; OCP-534 business = the Sec. 14 BGB business-capacity declaration
// (AGB V4 Präambel B). Labels live under `tabs.*` in the settings catalogue.
const TABS: Tab[] = ['profile', 'contact', 'business', 'terms', 'security', 'team'];

export function SettingsPage() {
  const { t } = useTranslation('settings');
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'profile';
  const setTab = useCallback(
    (next: Tab) => {
      const p = new URLSearchParams(params);
      p.set('tab', next);
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {t('intro')}
        </p>
      </div>

      <nav
        className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-800"
        aria-label={t('sectionsAria')}
      >
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            aria-pressed={tab === id}
            data-testid={`tab-${id}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
              tab === id
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            {t(`tabs.${id}`)}
          </button>
        ))}
      </nav>

      {tab === 'profile' && <ProfileTab />}
      {tab === 'contact' && <ResponsibleContactTab />}
      {tab === 'business' && <BusinessCapacityTab />}
      {tab === 'terms' && <TermsSettingsTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'team' && <TeamTab />}
    </div>
  );
}

// ---- shared bits ----------------------------------------------------

function ErrorAlert({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
    >
      {children}
    </div>
  );
}

function SuccessNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="status"
      className="mb-4 rounded border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300"
    >
      {children}
    </div>
  );
}

const inputCls =
  'mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm disabled:opacity-60';
const primaryBtn =
  'rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400';
const secondaryBtn =
  'rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-60';

// ---- Profile tab ----------------------------------------------------

interface AccountProfile {
  email: string;
  display_name: string | null;
  company_name: string | null;
  role: PortalRole;
}

function ProfileTab() {
  const { t } = useTranslation('settings');
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const p = await api<AccountProfile>('/v1/portal/settings/profile', { signal: ac.signal });
        setProfile(p);
        setDisplayName(p.display_name ?? '');
        setCompanyName(p.company_name ?? '');
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(t('profile.loadFailed'));
      }
    })();
    return () => ac.abort();
  }, []);

  const editable = canManage(profile?.role ?? null);

  const onSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSavePending(true);
      setError(null);
      setSaved(false);
      try {
        const updated = await api<AccountProfile>('/v1/portal/settings/profile', {
          method: 'PATCH',
          body: { display_name: displayName || null, company_name: companyName || null },
        });
        setProfile(updated);
        setSaved(true);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setError(t('profile.roleCannotEdit'));
        } else if (err instanceof ApiError) {
          setError(t('profile.saveFailedCode', { code: err.code ?? err.status }));
        } else {
          setError(t('profile.saveFailed'));
        }
      } finally {
        setSavePending(false);
      }
    },
    [displayName, companyName, t],
  );

  if (!profile && !error) {
    return <p className="text-sm text-gray-500">{t('profile.loading')}</p>;
  }

  return (
    <div className="space-y-5">
    <LanguageSection />
    <form onSubmit={onSave} className="space-y-5" data-testid="profile-form">
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {saved && <SuccessNote>{t('profile.saved')}</SuccessNote>}

      <label className="block text-sm">
        <span className="text-gray-700 dark:text-gray-300">{t('profile.email')}</span>
        <input type="email" value={profile?.email ?? ''} disabled readOnly className={inputCls} />
        <span className="mt-1 block text-xs text-gray-500">
          {t('profile.emailHint')}
        </span>
      </label>

      <label className="block text-sm">
        <span className="text-gray-700 dark:text-gray-300">{t('profile.displayName')}</span>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={!editable}
          maxLength={120}
          className={inputCls}
          data-testid="profile-display-name"
        />
      </label>

      <label className="block text-sm">
        <span className="text-gray-700 dark:text-gray-300">{t('profile.company')}</span>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          disabled={!editable}
          maxLength={120}
          className={inputCls}
          data-testid="profile-company-name"
        />
        <span className="mt-1 block text-xs text-gray-500">
          {t('profile.companyHint')}
        </span>
      </label>

      {editable ? (
        <div className="flex justify-end">
          <button type="submit" disabled={savePending} className={primaryBtn} data-testid="profile-save">
            {savePending ? t('profile.saving') : t('profile.save')}
          </button>
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          {t('profile.viewOnly', { role: roleLabel(t, profile!.role) })}
        </p>
      )}
    </form>
    </div>
  );
}

// ---- Responsible contact tab (OCP-524) ------------------------------
//
// The orboto Terms oblige the account holder to name a responsible contact
// person - somebody authorized to make and receive declarations on their
// behalf - and allow changing that person by a notice in text form. This tab
// is the customer's view of both halves: who is named right now, and the
// append-only record of every change with the confirmation mail that documents
// it. The history is shown to the CUSTOMER on purpose - it is their evidence
// as much as ours, and seeing it is how they notice an unexpected change.

function ResponsibleContactTab() {
  const { t } = useTranslation('settings');
  const [data, setData] = useState<ResponsibleContactResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const r = await api<ResponsibleContactResponse>(RESPONSIBLE_CONTACT_PATH, { signal });
      setData(r);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(t('contact.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const editable = canManageResponsibleContact(data?.role ?? null);

  if (!data && !error) {
    return <p className="text-sm text-gray-500">{t('contact.loading')}</p>;
  }

  return (
    <div className="space-y-6" data-testid="responsible-contact-tab">
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {saved && <SuccessNote>{t('contact.saved')}</SuccessNote>}

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              {t('contact.current')}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {t('contact.currentHint')}
            </p>
          </div>
          {editable && (
            <button
              type="button"
              onClick={() => {
                setSaved(false);
                setEditing(true);
              }}
              className={secondaryBtn}
              data-testid="responsible-contact-edit"
            >
              {data?.contact ? t('contact.change') : t('contact.name')}
            </button>
          )}
        </div>

        {data?.contact ? (
          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label={t('contact.field.name')} value={data.contact.name} />
            <Field label={t('contact.field.email')} value={data.contact.email} />
            <Field label={t('contact.field.phone')} value={data.contact.phone ?? '-'} />
            <Field label={t('contact.field.position')} value={data.contact.position ?? '-'} />
            <Field label={t('contact.field.namedOn')} value={formatContactDate(data.contact.set_at)} />
            <Field
              label={t('contact.field.namedBy')}
              value={
                data.contact.source === 'operator'
                  ? t('contact.recordedByOperator', { by: data.contact.set_by })
                  : data.contact.set_by
              }
            />
          </dl>
        ) : (
          <div className="mt-4 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-6 text-center text-sm text-gray-500">
            {t('contact.none')}
          </div>
        )}

        {!editable && (
          <p className="mt-4 text-xs text-gray-500">
            {t('contact.viewOnly', { role: roleLabel(t, data!.role) })}
          </p>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('contact.history')}
        </h2>
        <p className="mb-3 text-xs text-gray-500">
          {t('contact.historyHint')}
        </p>
        {!data || data.history.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
            {t('contact.noChanges')}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t('contact.col.when')}</th>
                  <th className="px-3 py-2 text-left">{t('contact.col.contact')}</th>
                  <th className="px-3 py-2 text-left">{t('contact.col.replaced')}</th>
                  <th className="px-3 py-2 text-left">{t('contact.col.by')}</th>
                  <th className="px-3 py-2 text-left">{t('contact.col.confirmation')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {data.history.map((h) => (
                  <tr key={h.id} data-testid={`contact-change-${h.id}`}>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-700 dark:text-gray-300">
                      {formatContactDate(h.changed_at)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-gray-900 dark:text-gray-100">{h.new_name}</div>
                      <div className="text-xs text-gray-500">{h.new_email}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {h.previous_name ?? h.previous_email ?? t('contact.firstNaming')}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {h.changed_by}
                      {h.source === 'operator' && (
                        <div className="text-[11px] text-gray-400">
                          {t('contact.recordedFromNotice')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {h.notice_mail_id ? (
                        <span className="text-emerald-700 dark:text-emerald-300">
                          {t('contact.sent', { id: h.notice_mail_id })}
                        </span>
                      ) : (
                        <span className="text-amber-700 dark:text-amber-300">
                          {t('contact.notDelivered')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && (
        <ResponsibleContactModal
          mode={data?.contact ? 'change' : 'gate'}
          initial={data?.contact ?? null}
          onCancel={() => setEditing(false)}
          onSaved={(r) => {
            setData(r);
            setEditing(false);
            setSaved(true);
          }}
        />
      )}
    </div>
  );
}

/**
 * OCP-534 - the AGB V4 Präambel B declaration on the self-service surface.
 *
 * The order dialog collects it at the moment of the contract conclusion; this
 * tab is where a customer sees what is on file, corrects the company name, and
 * where the prompt banner sends pre-decision accounts. Same
 * `BusinessCapacityForm` as the dialog, so the declared wording cannot differ
 * between the two entry points.
 */
function BusinessCapacityTab() {
  const { t } = useTranslation('settings');
  const [data, setData] = useState<BusinessCapacityResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setData(await api<BusinessCapacityResponse>(BUSINESS_CAPACITY_PATH, { signal }));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(t('business.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const editable = canDeclareBusinessCapacity(data?.role);

  if (!data && !error) {
    return <p className="text-sm text-gray-500">{t('business.loading')}</p>;
  }

  return (
    <div className="space-y-6" data-testid="business-capacity-tab">
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {saved && <SuccessNote>{t('business.saved')}</SuccessNote>}

      {data?.blocked_reason === 'business_capacity_refused' && (
        <BusinessCapacityRefusalNotice refusal={data.refusal} />
      )}

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              {t('business.yourDeclaration')}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {t('business.declarationHint')}
            </p>
          </div>
          {editable && data?.blocked_reason !== 'business_capacity_refused' && (
            <button
              type="button"
              onClick={() => {
                setSaved(false);
                setEditing(true);
              }}
              className={secondaryBtn}
              data-testid="business-capacity-edit"
            >
              {data?.declaration ? t('business.update') : t('business.confirmNow')}
            </button>
          )}
        </div>

        {data?.declaration ? (
          <>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label={t('business.field.company')} value={data.company_name ?? '-'} />
              <Field label={t('business.field.vatId')} value={data.vat_id ?? t('business.notProvided')} />
              <Field
                label={t('business.field.confirmedOn')}
                value={formatDeclarationDate(data.declaration.confirmed_at)}
              />
              <Field label={t('business.field.confirmedBy')} value={data.declaration.confirmed_by} />
            </dl>
            <blockquote
              className="mt-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3 text-xs text-gray-600 dark:text-gray-400"
              data-testid="business-capacity-statement"
            >
              {data.declaration.statement}
            </blockquote>
            <p className="mt-2 text-xs text-gray-500">
              {t('business.storedWording')}
            </p>
          </>
        ) : (
          <div className="mt-4 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-6 text-center text-sm text-gray-500">
            {t('business.none')}
          </div>
        )}

        {data?.blocked_reason === 'company_name_missing' && (
          <p className="mt-4 text-xs text-amber-700 dark:text-amber-300">
            {t('business.companyMissing')}
          </p>
        )}

        {!editable && data && (
          <p className="mt-4 text-xs text-gray-500">
            {t('business.viewOnly', { role: roleLabel(t, data.role) })}
          </p>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('business.whyWeAsk')}
        </h2>
        <p className="text-xs text-gray-500">
          {t('business.whyWeAskBody')}
        </p>
      </section>

      {editing && data && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('business.dialogAria')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
          onClick={() => setEditing(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl"
          >
            <h2 className="text-base font-semibold">{t('business.dialogTitle')}</h2>
            <BusinessCapacityForm
              initial={data}
              submitLabel={t('business.confirm')}
              onCancel={() => setEditing(false)}
              onSaved={(r) => {
                setData(r);
                setEditing(false);
                setSaved(true);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

function formatContactDate(iso: string): string {
  return fmtDateTime(iso, iso);
}

// ---- Security tab ---------------------------------------------------

interface SecurityStatus {
  totp_enabled: boolean;
  active_sessions: number;
}

function SecurityTab() {
  const { t } = useTranslation('settings');
  const [status, setStatus] = useState<SecurityStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async (signal?: AbortSignal) => {
    const s = await api<SecurityStatus>('/v1/portal/settings/security/status', { signal });
    setStatus(s);
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        await loadStatus(ac.signal);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(t('security.loadFailed'));
      }
    })();
    return () => ac.abort();
  }, [loadStatus, t]);

  if (!status && !error) {
    return <p className="text-sm text-gray-500">{t('security.loading')}</p>;
  }

  return (
    <div className="space-y-8">
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {status && (
        <TwoFactorSection status={status} onChanged={() => void loadStatus()} />
      )}
      <PasswordChangeSection />
      {status && (
        <p className="text-xs text-gray-500" data-testid="active-sessions">
          {t('security.activeSessions', { count: status.active_sessions })}
        </p>
      )}
    </div>
  );
}

/**
 * OCP-781 - 2FA set-up, replacement and switch-off all re-authenticate:
 * set-up asks for the password, replacing and switching off ask for the
 * password AND a current code (authenticator or backup code). The secret is
 * generated and kept by the server for this session (ten minutes); confirm
 * sends only the code. Every other session is signed out on a change.
 */
function TwoFactorSection({
  status,
  onChanged,
}: {
  status: SecurityStatus;
  onChanged: () => void;
}) {
  const { t } = useTranslation('settings');
  // reauth = password (+ current code when replacing) before the server issues a secret
  const [phase, setPhase] = useState<'idle' | 'reauth' | 'enrolling'>('idle');
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [reauthPassword, setReauthPassword] = useState('');
  const [currentCode, setCurrentCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const replacing = status.totp_enabled;

  const errorFor = useCallback(
    (err: unknown, fallback: string): string => {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_credentials') return t('security.error.passwordIncorrect');
        if (err.code === 'totp_invalid') return t('security.error.totpInvalid');
        if (err.code === 'totp_reauth_required') return t('security.error.reauthRequired');
        if (err.code === 'totp_enrollment_expired' || err.code === 'totp_state_changed') return t('security.error.enrollmentExpired');
      }
      return fallback;
    },
    [t],
  );

  const resetReauth = () => {
    setReauthPassword('');
    setCurrentCode('');
  };

  const begin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setPending(true);
      try {
        const r = await api<{ secret: string; qr_data_uri: string }>(
          '/v1/portal/settings/security/totp/begin',
          { method: 'POST', body: replacing ? { password: reauthPassword, current_code: currentCode } : { password: reauthPassword } },
        );
        setSecret(r.secret);
        setQr(r.qr_data_uri);
        resetReauth();
        setPhase('enrolling');
      } catch (err) {
        setError(errorFor(err, t('security.error.beginFailed')));
      } finally {
        setPending(false);
      }
    },
    [replacing, reauthPassword, currentCode, errorFor, t],
  );

  const confirm = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setPending(true);
      try {
        const r = await api<{ backup_codes: string[] }>(
          '/v1/portal/settings/security/totp/confirm',
          { method: 'POST', body: { code } },
        );
        setBackupCodes(r.backup_codes);
        setPhase('idle');
        setCode('');
        setSecret('');
        setQr('');
        onChanged();
      } catch (err) {
        if (err instanceof ApiError && (err.code === 'totp_enrollment_expired' || err.code === 'totp_state_changed')) setPhase('idle');
        setError(errorFor(err, t('security.error.confirmFailed')));
      } finally {
        setPending(false);
      }
    },
    [code, onChanged, errorFor, t],
  );

  const disable = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setPending(true);
      try {
        await api('/v1/portal/settings/security/totp/disable', {
          method: 'POST',
          body: { password: disablePassword, code: disableCode },
        });
        setShowDisable(false);
        setDisablePassword('');
        setDisableCode('');
        onChanged();
      } catch (err) {
        setError(errorFor(err, t('security.error.disableFailed')));
      } finally {
        setPending(false);
      }
    },
    [disablePassword, disableCode, onChanged, errorFor, t],
  );

  return (
    <section data-testid="twofactor-section">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {t('security.twoFactor')}
        </h2>
        <span
          data-testid="twofactor-state"
          className={`rounded px-2 py-0.5 text-xs font-medium ${
            status.totp_enabled
              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          {status.totp_enabled ? t('security.on') : t('security.off')}
        </span>
      </div>

      {error && <ErrorAlert>{error}</ErrorAlert>}

      {backupCodes && (
        <div
          data-testid="backup-codes"
          className="mb-4 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4"
        >
          <p className="mb-2 text-sm font-medium text-amber-900 dark:text-amber-200">
            {t('security.backupCodesHint')}
          </p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm text-amber-900 dark:text-amber-200 sm:grid-cols-3">
            {backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {!status.totp_enabled && phase === 'idle' && (
        <div>
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
            {t('security.protectHint')}
          </p>
          <button
            type="button"
            onClick={() => setPhase('reauth')}
            disabled={pending}
            className={primaryBtn}
            data-testid="enable-2fa"
          >
            {t('security.enable')}
          </button>
        </div>
      )}

      {phase === 'reauth' && (
        <form onSubmit={begin} className="space-y-3" data-testid="reauth-form">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {replacing ? t('security.reauthReplaceHint') : t('security.reauthEnableHint')}
          </p>
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('security.currentPassword')}</span>
            <input
              type="password"
              autoComplete="current-password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              className={inputCls}
              data-testid="reauth-password"
            />
          </label>
          {replacing && (
            <label className="block text-sm">
              <span className="text-gray-700 dark:text-gray-300">{t('security.currentCode')}</span>
              <input
                type="text"
                autoComplete="one-time-code"
                value={currentCode}
                onChange={(e) => setCurrentCode(e.target.value.trim().slice(0, 32))}
                placeholder={t('security.codePlaceholder')}
                className={inputCls}
                data-testid="reauth-code"
              />
            </label>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={pending || !reauthPassword || (replacing && currentCode.length < 6)}
              className={primaryBtn}
              data-testid="reauth-continue"
            >
              {pending ? t('security.starting') : t('security.continue')}
            </button>
            <button
              type="button"
              onClick={() => {
                resetReauth();
                setPhase('idle');
              }}
              className={secondaryBtn}
            >
              {t('security.cancel')}
            </button>
          </div>
        </form>
      )}

      {phase === 'enrolling' && (
        <form onSubmit={confirm} className="space-y-3" data-testid="enroll-form">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('security.scanHint')}
          </p>
          {qr && <img src={qr} alt={t('security.qrAlt')} width={160} height={160} className="rounded border border-gray-200 dark:border-gray-800" />}
          <p className="text-xs text-gray-500">
            {t('security.manualSecret')}<code className="font-mono">{secret}</code>
          </p>
          <p className="text-xs text-gray-500" data-testid="enroll-signout-note">
            {t('security.signOutOthersHint')}
          </p>
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('security.verificationCode')}</span>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder={t('security.codePlaceholder')}
              className={inputCls}
              data-testid="totp-code"
            />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={pending || code.length < 6} className={primaryBtn} data-testid="confirm-2fa">
              {pending ? t('security.confirming') : t('security.confirm')}
            </button>
            <button type="button" onClick={() => setPhase('idle')} className={secondaryBtn}>
              {t('security.cancel')}
            </button>
          </div>
        </form>
      )}

      {status.totp_enabled && !showDisable && phase === 'idle' && (
        <div>
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
            {t('security.protecting')}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setPhase('reauth')}
              className={secondaryBtn}
              data-testid="replace-2fa"
            >
              {t('security.replace')}
            </button>
            <button
              type="button"
              onClick={() => setShowDisable(true)}
              className={secondaryBtn}
              data-testid="disable-2fa"
            >
              {t('security.disable')}
            </button>
          </div>
        </div>
      )}

      {status.totp_enabled && showDisable && (
        <form onSubmit={disable} className="space-y-3" data-testid="disable-form">
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">
              {t('security.confirmPasswordToDisable')}
            </span>
            <input
              type="password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              className={inputCls}
              data-testid="disable-password"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('security.currentCode')}</span>
            <input
              type="text"
              autoComplete="one-time-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.trim().slice(0, 32))}
              placeholder={t('security.codePlaceholder')}
              className={inputCls}
              data-testid="disable-code"
            />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={pending || !disablePassword || disableCode.length < 6} className={primaryBtn} data-testid="confirm-disable">
              {pending ? t('security.disabling') : t('security.disable')}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowDisable(false);
                setDisablePassword('');
                setDisableCode('');
              }}
              className={secondaryBtn}
            >
              {t('security.cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function PasswordChangeSection() {
  const { t } = useTranslation('settings');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [pending, setPending] = useState(false);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setSaved(false);
      if (next !== confirm) {
        setError(t('security.password.error.mismatch'));
        return;
      }
      setPending(true);
      try {
        await api('/v1/portal/settings/security/password', {
          method: 'POST',
          body: { current_password: current, new_password: next },
        });
        setSaved(true);
        setCurrent('');
        setNext('');
        setConfirm('');
      } catch (err) {
        if (err instanceof ApiError && err.code === 'invalid_credentials') {
          setError(t('security.password.error.currentIncorrect'));
        } else if (err instanceof ApiError && err.code === 'weak_password') {
          setError(t('security.password.error.weak'));
        } else {
          setError(t('security.password.error.generic'));
        }
      } finally {
        setPending(false);
      }
    },
    [current, next, confirm, t],
  );

  return (
    <section data-testid="password-section">
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
        {t('security.password.title')}
      </h2>
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {saved && <SuccessNote>{t('security.password.saved')}</SuccessNote>}
      <form onSubmit={onSubmit} className="space-y-3 max-w-sm">
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('security.password.current')}</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={inputCls}
            data-testid="current-password"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('security.password.new')}</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={inputCls}
            data-testid="new-password"
          />
          <span className="mt-1 block text-xs text-gray-500">{t('security.password.newHint')}</span>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('security.password.confirm')}</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputCls}
            data-testid="confirm-password"
          />
        </label>
        <button
          type="submit"
          disabled={pending || !current || !next || !confirm}
          className={primaryBtn}
          data-testid="change-password"
        >
          {pending ? t('security.password.saving') : t('security.password.submit')}
        </button>
      </form>
    </section>
  );
}

// ---- Team tab -------------------------------------------------------

interface TeamMember {
  id: string;
  email: string;
  role: PortalRole;
  email_verified: boolean;
  pending_setup: boolean;
  last_login_at: string | null;
  created_at: string;
  active_sessions: number;
}

interface TeamResponse {
  members: TeamMember[];
  self_id: string;
  self_role: PortalRole;
}

function TeamTab() {
  const { t } = useTranslation('settings');
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Exclude<PortalRole, 'owner'>>('admin');
  const [invitePending, setInvitePending] = useState(false);
  const [inviteNote, setInviteNote] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    const t = await api<TeamResponse>('/v1/portal/settings/team', { signal });
    setTeam(t);
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        await load(ac.signal);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(t('team.loadFailed'));
      }
    })();
    return () => ac.abort();
  }, [load, t]);

  const manage = canManage(team?.self_role ?? null);

  const onInvite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setInviteNote(null);
      setInvitePending(true);
      try {
        await api('/v1/portal/settings/team/invite', {
          method: 'POST',
          body: { email: inviteEmail, role: inviteRole },
        });
        setInviteEmail('');
        setInviteNote(t('team.inviteSent', { email: inviteEmail }));
        await load();
      } catch (err) {
        if (err instanceof ApiError && err.code === 'email_in_use') {
          setError(t('team.error.emailInUse'));
        } else if (err instanceof ApiError) {
          setError(t('team.error.inviteFailedCode', { code: err.code ?? err.status }));
        } else {
          setError(t('team.error.inviteFailed'));
        }
      } finally {
        setInvitePending(false);
      }
    },
    [inviteEmail, inviteRole, load, t],
  );

  const onRoleChange = useCallback(
    async (member: TeamMember, role: Exclude<PortalRole, 'owner'>) => {
      setError(null);
      try {
        await api(`/v1/portal/settings/team/${member.id}/role`, {
          method: 'PATCH',
          body: { role },
        });
        await load();
      } catch (err) {
        if (err instanceof ApiError) setError(t('team.error.roleFailedCode', { code: err.code ?? err.status }));
        else setError(t('team.error.roleFailed'));
      }
    },
    [load, t],
  );

  const onRemove = useCallback(
    async (member: TeamMember) => {
      if (!window.confirm(t('team.removeConfirm', { email: member.email }))) return;
      setError(null);
      try {
        await api(`/v1/portal/settings/team/${member.id}`, { method: 'DELETE' });
        await load();
      } catch (err) {
        if (err instanceof ApiError) setError(t('team.error.removeFailedCode', { code: err.code ?? err.status }));
        else setError(t('team.error.removeFailed'));
      }
    },
    [load, t],
  );

  if (!team && !error) {
    return <p className="text-sm text-gray-500">{t('team.loading')}</p>;
  }

  return (
    <section className="space-y-6">
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {inviteNote && <SuccessNote>{inviteNote}</SuccessNote>}

      {manage && (
        <form
          onSubmit={onInvite}
          className="flex flex-col gap-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 sm:flex-row sm:items-end"
          data-testid="invite-form"
        >
          <label className="block flex-1 text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('team.invite')}</span>
            <input
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t('team.invitePlaceholder')}
              className={inputCls}
              data-testid="invite-email"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700 dark:text-gray-300">{t('team.role')}</span>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Exclude<PortalRole, 'owner'>)}
              className={inputCls}
              data-testid="invite-role"
            >
              {INVITABLE.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(t, r)}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={invitePending} className={primaryBtn} data-testid="invite-submit">
            {invitePending ? t('team.sending') : t('team.sendInvite')}
          </button>
        </form>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">{t('team.col.member')}</th>
              <th className="px-3 py-2 text-left">{t('team.col.role')}</th>
              <th className="px-3 py-2 text-left">{t('team.col.status')}</th>
              {manage && <th className="px-3 py-2 text-right">{t('team.col.actions')}</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {team?.members.map((m) => {
              const isSelf = m.id === team.self_id;
              const isOwner = m.role === 'owner';
              const rowManageable = manage && !isOwner && !isSelf;
              return (
                <tr key={m.id} data-testid={`member-row-${m.id}`}>
                  <td className="px-3 py-2">
                    <span className="text-gray-900 dark:text-gray-100">{m.email}</span>
                    {isSelf && <span className="ml-2 text-xs text-gray-500">{t('team.you')}</span>}
                  </td>
                  <td className="px-3 py-2">
                    {rowManageable ? (
                      <select
                        value={m.role}
                        onChange={(e) =>
                          void onRoleChange(m, e.target.value as Exclude<PortalRole, 'owner'>)
                        }
                        className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
                        data-testid={`member-role-${m.id}`}
                      >
                        {INVITABLE.map((r) => (
                          <option key={r} value={r}>
                            {roleLabel(t, r)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-gray-700 dark:text-gray-300">{roleLabel(t, m.role)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {m.pending_setup ? (
                      <span className="rounded bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-900 dark:text-amber-200">
                        {t('team.invitePending')}
                      </span>
                    ) : (
                      <span className="rounded bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                        {t('team.active')}
                      </span>
                    )}
                  </td>
                  {manage && (
                    <td className="px-3 py-2 text-right">
                      {rowManageable ? (
                        <button
                          type="button"
                          onClick={() => void onRemove(m)}
                          className="text-sm text-red-700 dark:text-red-400 hover:underline"
                          data-testid={`member-remove-${m.id}`}
                        >
                          {t('team.remove')}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!manage && (
        <p className="text-xs text-gray-500">
          {t('team.viewOnly', { role: roleLabel(t, team!.self_role) })}
        </p>
      )}
    </section>
  );
}
